/**
 * Font readiness for canvas text.
 *
 * Fabric measures glyphs when a text object is created or changed, and it
 * caches those widths per font family. A face that is still loading at that
 * moment is measured — and drawn — in the browser's fallback font, and the
 * browser-rendered design crop that production prints from carries the
 * fallback too. These helpers resolve once every face a text uses is ready
 * (family + weight + style, for the glyphs it actually contains) and then
 * re-measure the texts that were drawn too early.
 */
import { cache, type Canvas } from 'fabric';

/** One face a text needs, in CSS Font Loading API terms. */
export interface TextFace {
  /** The object's fontFamily as stored (may be a stack: "Roboto, sans-serif"). */
  fontFamily: string;
  /** CSS font shorthand for document.fonts: `italic 700 16px "Roboto"`. */
  descriptor: string;
  /** The glyphs to load — unicode-range subsets only load for used chars. */
  text: string;
}

/** Shape shared by Fabric text instances and their serialized JSON. */
interface TextLike {
  fontFamily?: unknown;
  fontWeight?: unknown;
  fontStyle?: unknown;
  text?: unknown;
  /** Serialized group children */
  objects?: unknown;
  /** Live group children */
  getObjects?: () => unknown[];
}

/** First family of a CSS font stack, unquoted: "'Open Sans', serif" → "Open Sans". */
export function primaryFontFamily(fontFamily: string): string {
  const first = fontFamily.split(',')[0] ?? '';
  return first.trim().replace(/^['"]|['"]$/g, '');
}

/** CSS font shorthand for the Font Loading API, weight- and style-aware. */
export function fontFaceDescriptor(
  fontFamily: string,
  fontWeight?: unknown,
  fontStyle?: unknown
): string {
  const weight =
    typeof fontWeight === 'number' ||
    (typeof fontWeight === 'string' && fontWeight)
      ? String(fontWeight)
      : 'normal';
  const style =
    typeof fontStyle === 'string' && fontStyle ? fontStyle : 'normal';
  return `${style} ${weight} 16px "${primaryFontFamily(fontFamily)}"`;
}

/**
 * Collect the faces used by text objects — live Fabric objects or their
 * serialized JSON, including group children. Deduplicated per face, with the
 * glyphs of every text using it merged.
 */
export function collectTextFaces(objects: readonly unknown[]): TextFace[] {
  const faces = new Map<string, { fontFamily: string; chars: Set<string> }>();

  const visit = (value: unknown) => {
    if (!value || typeof value !== 'object') return;
    const obj = value as TextLike;

    if (typeof obj.fontFamily === 'string' && obj.fontFamily) {
      const descriptor = fontFaceDescriptor(
        obj.fontFamily,
        obj.fontWeight,
        obj.fontStyle
      );
      const entry = faces.get(descriptor) ?? {
        fontFamily: obj.fontFamily,
        chars: new Set<string>(),
      };
      if (typeof obj.text === 'string') {
        for (const char of obj.text) entry.chars.add(char);
      }
      faces.set(descriptor, entry);
    }

    const children =
      typeof obj.getObjects === 'function' ? obj.getObjects() : obj.objects;
    if (Array.isArray(children)) children.forEach(visit);
  };

  objects.forEach(visit);

  return [...faces].map(([descriptor, { fontFamily, chars }]) => ({
    fontFamily,
    descriptor,
    // A lone space still loads the face's basic subset.
    text: [...chars].join('').trim() ? [...chars].join('') : ' ',
  }));
}

/** Faces the browser has not loaded yet (system fonts count as loaded). */
function pendingFaces(faces: readonly TextFace[]): TextFace[] {
  // No FontFaceSet (very old browsers, some test environments): nothing to wait for.
  const fontSet = typeof document === 'undefined' ? undefined : document.fonts;
  if (!fontSet) return [];
  return faces.filter((face) => {
    try {
      return !fontSet.check(face.descriptor, face.text);
    } catch {
      return false;
    }
  });
}

/**
 * Resolve once every given face is loaded. Never rejects: a face that fails
 * to load falls back visually, which beats blocking the edit or the save.
 *
 * @returns whether anything had to be loaded (so earlier measurements may be stale)
 */
export async function ensureFontsLoaded(
  faces: readonly TextFace[]
): Promise<boolean> {
  const pending = pendingFaces(faces);
  if (pending.length === 0) return false;
  await Promise.all(
    pending.map((face) =>
      document.fonts.load(face.descriptor, face.text).catch(() => [])
    )
  );
  return true;
}

/**
 * Make sure every text on the canvas is drawn in its real face before the
 * canvas is exported (crop, preview, thumbnail).
 *
 * Returns `null` when nothing is pending, so callers that must stay
 * synchronous in the common case (a save that captures the canvas before a
 * step change disposes it) only yield when a face is actually missing.
 */
export function ensureCanvasFontsLoaded(canvas: Canvas): Promise<void> | null {
  const faces = collectTextFaces(canvas.getObjects());
  if (pendingFaces(faces).length === 0) return null;

  return ensureFontsLoaded(faces).then((loaded) => {
    if (!loaded || canvas.destroyed) return;
    // Fabric cached the fallback's glyph widths: drop them and measure again.
    new Set(faces.map((face) => face.fontFamily)).forEach((family) =>
      cache.clearFontCache(family)
    );
    remeasureTexts(canvas.getObjects());
    canvas.requestRenderAll();
  });
}

function remeasureTexts(objects: readonly unknown[]): void {
  for (const value of objects) {
    const obj = value as TextLike & {
      initDimensions?: () => void;
      setCoords?: () => void;
    };
    if (typeof obj.fontFamily === 'string' && obj.initDimensions) {
      obj.initDimensions();
      obj.setCoords?.();
    }
    if (typeof obj.getObjects === 'function') remeasureTexts(obj.getObjects());
  }
}
